import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FaEye, FaEyeSlash, FaLock, FaUser } from "react-icons/fa"; 
import { FiMail } from "react-icons/fi"; 

import { useTheme } from "../contexts/ThemeContext";
import { THEMES, THEME_COLORS, getLogoPath } from "../components/Constants";
import { authFetch } from "../utils/api";

import LogoColumnBlue from '../images/logo_column_blue.png';
import LogoColumnGreen from '../images/logo_column_green.png';
import LogoColumnRed from '../images/logo_column_red.png';
import LogoColumnOrange from '../images/logo_column_orange.png';
import LogoColumnYellow from '../images/logo_column_yellow.png';

const LOGO_COLUMN_IMAGES = {
    'logo_column_blue.png': LogoColumnBlue,
    'logo_column_green.png': LogoColumnGreen,
    'logo_column_red.png': LogoColumnRed,
    'logo_column_orange.png': LogoColumnOrange,
    'logo_column_yellow.png': LogoColumnYellow,
};

export default function RegisterPage() {
    const { currentTheme } = useTheme();
    const themeColors = THEME_COLORS[currentTheme] || THEME_COLORS[THEMES.BLUE];
    const [searchParams, setSearchParams] = useSearchParams();

    // 1. L'état du plan est désormais modifiable localement
    const [selectedPlan, setSelectedPlan] = useState("TRIAL");

    const [isLoading, setIsLoading] = useState(false);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [confirmEmail, setConfirmEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [agreed, setAgreed] = useState(false); 
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const logoFileName = getLogoPath(currentTheme, 'column');
    const LogoImage = LOGO_COLUMN_IMAGES[logoFileName];

    // 2. Synchronisation initiale avec l'URL provenant de la Landing Page
    useEffect(() => {
        const planFromUrl = searchParams.get("plan");
        if (planFromUrl && ["TRIAL", "STANDARD", "PREMIUM"].includes(planFromUrl.toUpperCase())) {
            setSelectedPlan(planFromUrl.toUpperCase());
        }
    }, [searchParams]);

    // 3. Permet de mettre à jour proprement l'URL si l'utilisateur change d'avis sur la page
    const handlePlanChange = (planName) => {
        setSelectedPlan(planName);
        setSearchParams({ plan: planName });
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (email.toLowerCase() !== confirmEmail.toLowerCase()) {
            setError("Les adresses e-mail ne correspondent pas.");
            return;
        }

        if (password !== confirmPassword) {
            setError("Les mots de passe ne correspondent pas.");
            return;
        }

        if (!agreed) {
            setError("Vous devez accepter les Mentions Légales et CGV.");
            return;
        }

        setIsLoading(true);

        try {
            const response = await authFetch.post("/register", { 
                name,
                email,
                password,
                plan: selectedPlan // Envoie le plan validé au moment du clic
            });

            const data = await response.json();

            if (!response.ok) {
                setError(data.error || "Erreur lors de l'inscription.");
                return;
            }

            if (data.stripeCheckoutUrl) {
                setSuccess("Compte créé ! Redirection vers Stripe pour le paiement sécurisé...");
                setTimeout(() => {
                    window.location.href = data.stripeCheckoutUrl;
                }, 2000);
                return;
            }

            setSuccess("Inscription réussie ! 🎉 Veuillez cliquer sur le lien envoyé par mail pour valider votre compte.");
            setName("");
            setEmail("");
            setConfirmEmail("");
            setPassword("");
            setConfirmPassword("");
            setAgreed(false);

        } catch (err) {
            console.error("❌ Erreur réseau :", err);
            setError("Impossible de contacter le serveur.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={styles.container}>
            <div style={{ ...styles.card, color: themeColors.text }}>
                <div style={styles.logoContainer}>
                    <img src={LogoImage} alt="Mon Budget App Logo" style={styles.logoImage} />
                </div>
                
                <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>Créer un compte</h2>
                
                {/* --- SÉLECTEUR D'ABONNEMENT INTERACTIF --- */}
                <div style={styles.planSelectorContainer}>
                    {["TRIAL", "STANDARD", "PREMIUM"].map((planName) => {
                        const isCurrent = selectedPlan === planName;
                        return (
                            <button
                                key={planName}
                                type="button"
                                onClick={() => handlePlanChange(planName)}
                                style={{
                                    ...styles.planTab,
                                    backgroundColor: isCurrent ? themeColors.primary : '#eee',
                                    color: isCurrent ? '#fff' : '#555',
                                    fontWeight: isCurrent ? 'bold' : 'normal',
                                    border: isCurrent ? `1px solid ${themeColors.primary}` : '1px solid #ccc'
                                }}
                            >
                                {planName}
                            </button>
                        );
                    })}
                </div>

                <form onSubmit={handleRegister}>
                    <div style={styles.inputGroup}>
                        <div style={styles.inputWrapper}>
                            <FaUser style={{ ...styles.inputIcon, color: themeColors.secondary }} />
                            <input type="text" value={name} onChange={(e) => setName(e.target.value)} style={{ ...styles.input, border: `1px solid ${themeColors.inputBorder}` }} placeholder="Nom" required />
                        </div>
                    </div>
                    <div style={styles.inputGroup}>
                        <div style={styles.inputWrapper}>
                            <FiMail style={{ ...styles.inputIcon, color: themeColors.secondary }} />
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={{ ...styles.input, border: `1px solid ${themeColors.inputBorder}` }} placeholder="E-mail" required />
                        </div>
                    </div>
                    <div style={styles.inputGroup}>
                        <div style={styles.inputWrapper}>
                            <FiMail style={{ ...styles.inputIcon, color: themeColors.secondary }} />
                            <input type="email" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} style={{ ...styles.input, border: `1px solid ${themeColors.inputBorder}` }} placeholder="Confirmer l'e-mail" required />
                        </div>
                    </div>
                    <div style={styles.inputGroup}>
                        <div style={styles.passwordWrapper}>
                            <FaLock style={{ ...styles.inputIcon, color: themeColors.secondary }} />
                            <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} style={{ ...styles.input, border: `1px solid ${themeColors.inputBorder}` }} placeholder="Mot de passe" required />
                            <span onClick={() => setShowPassword(!showPassword)} style={{ ...styles.eyeIcon, color: themeColors.secondary }}>
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </span>
                        </div>
                    </div>
                    <div style={styles.inputGroup}>
                        <div style={styles.passwordWrapper}>
                            <FaLock style={{ ...styles.inputIcon, color: themeColors.secondary }} />
                            <input type={showConfirmPassword ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} style={{ ...styles.input, border: `1px solid ${themeColors.inputBorder}` }} placeholder="Confirmer le mot de passe" required />
                            <span onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ ...styles.eyeIcon, color: themeColors.secondary }}>
                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                            </span>
                        </div>
                    </div>

                    <div style={styles.checkboxContainer}>
                        <input type="checkbox" id="agreed" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={styles.checkbox} />
                        <label htmlFor="agreed" style={styles.checkboxLabel}>
                            J'accepte les <Link to="/mentions-legales" target="_blank" style={{color: themeColors.secondary}}>Mentions Légales</Link> et les <Link to="/cgv" target="_blank" style={{color: themeColors.secondary}}>CGV / CGU</Link>.
                        </label>
                    </div>

                    {error && <p style={{ color: themeColors.danger, textAlign: 'center', fontWeight: 'bold', fontSize: '0.9rem' }}>{error}</p>}
                    {success && <p style={{ color: themeColors.success, textAlign: 'center', fontWeight: 'bold', fontSize: '0.9rem' }}>{success}</p>}
                    
                    <button type="submit" disabled={isLoading} style={{ ...styles.button, backgroundColor: themeColors.secondary, cursor: isLoading ? 'not-allowed' : 'pointer' }}>
                        {isLoading ? "Veuillez patienter..." : "S'inscrire"}
                    </button>
                </form>
                
                <p style={{ textAlign: "center", marginTop: 15, color: themeColors.text }}>
                    Déjà un compte ? <Link to="/login" style={{ ...styles.link, color: themeColors.secondary }}>Se connecter</Link>
                </p>
                <p style={{ textAlign: "center", marginTop: 15, paddingTop: 15, borderTop: '1px solid #eee' }}>
                    <Link to="/" style={{ ...styles.link, color: themeColors.text, fontSize: '0.9rem', opacity: 0.8, fontWeight: 'normal' }}>← Retour à la page d'accueil</Link>
                </p>
            </div>
        </div>
    );
}

const styles = {
    container: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", padding: "20px 15px", boxSizing: "border-box" },
    card: { padding: 30, borderRadius: 20, boxShadow: "0 4px 15px rgba(0,0,0,0.15)", width: "100%", maxWidth: 400, boxSizing: "border-box", backgroundColor: '#fcfcfc' },
    planSelectorContainer: { display: 'flex', justifyContent: 'space-between', gap: '5px', marginBottom: '20px', backgroundColor: '#eee', padding: '4px', borderRadius: '10px' },
    planTab: { flex: 1, padding: '8px 5px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', transition: 'all 0.2s ease', textAlign: 'center' },
    inputGroup: { marginBottom: 15 },
    input: { width: "100%", padding: "12px 12px 12px 40px", fontSize: 16, borderRadius: 10, boxSizing: "border-box", outline: 'none' },
    passwordWrapper: { position: "relative", width: "100%" },
    eyeIcon: { position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", cursor: "pointer", zIndex: 2 },
    button: { width: "100%", padding: 12, fontSize: 16, color: "#fff", border: "none", borderRadius: 10, fontWeight: 'bold', marginTop: 10 },
    logoContainer: { textAlign: "center", marginBottom: 15 },
    logoImage: { maxWidth: "90px", height: "auto" },
    inputWrapper: { position: "relative", width: "100%" },
    inputIcon: { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: '18px', zIndex: 1 },
    link: { textDecoration: 'none', fontWeight: 'bold' },
    checkboxContainer: { display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '15px', marginTop: '5px' },
    checkbox: { marginTop: '3px', cursor: 'pointer' },
    checkboxLabel: { fontSize: '0.85rem', color: '#444', lineHeight: '1.4' }
};